import React, {useState, useEffect} from 'react';
import {HashRouter as Router, Routes, Route} from 'react-router-dom';
import {motion} from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import CaseStudies from './pages/CaseStudies';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import {isAuthenticated, setupAutoLogout} from './utils/auth';
import {initializeSamplePosts} from './utils/blogStorage';
import {initializeSampleCaseStudies} from './utils/caseStudyStorage';
import {initializeSampleAuthors} from './utils/authorStorage';
import {initializeAdminConfig} from './utils/adminConfig';
import {initializeDatabase} from './utils/database';
import {initializeAnalytics, initializeMarketing, initializeFunctional} from './utils/cookieManager';
import './App.css';

function App() {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dbConnected, setDbConnected] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('🚀 Initializing aiximo application...');

        // Initialize database connection
        const connected = await initializeDatabase();
        setDbConnected(connected);
        console.log(`📊 Database: ${connected ? 'Supabase Connected' : 'Using localStorage fallback'}`);

        // Initialize configurations and sample data
        initializeAdminConfig();
        console.log('⚙️ Admin configuration initialized');

        // Initialize sample authors first (needed for blog posts)
        const authorsInitialized = await initializeSampleAuthors();
        console.log(`👥 Authors: ${authorsInitialized ? 'Sample authors created' : 'Using existing authors'}`);

        // Initialize sample blog posts
        const postsInitialized = await initializeSamplePosts();
        console.log(`📝 Blog posts: ${postsInitialized ? 'Sample posts created' : 'Using existing posts'}`);

        // Initialize sample case studies
        const studiesInitialized = await initializeSampleCaseStudies();
        console.log(`💼 Case studies: ${studiesInitialized ? 'Sample studies created' : 'Using existing studies'}`);

        // Check if admin is already authenticated
        setIsAdminAuthenticated(isAuthenticated());

        // Setup auto-logout for admin sessions
        if (isAuthenticated()) {
          setupAutoLogout(() => {
            setIsAdminAuthenticated(false);
          });
        }

        // Initialize cookie-dependent services
        initializeAnalytics();
        initializeMarketing();
        initializeFunctional();

        console.log('✅ Application initialization complete');
        setIsInitialized(true);
      } catch (error) {
        console.error('❌ App initialization error:', error);
        setIsInitialized(true); // Still allow app to load with localStorage fallback
      }
    };

    initializeApp();
  }, []);

  const handleAdminLogin = (token) => {
    setIsAdminAuthenticated(true);
    // Setup auto-logout after successful login
    setupAutoLogout(() => {
      setIsAdminAuthenticated(false);
    });
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
  };

  // Show loading screen while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <motion.div
          initial={{opacity: 0, scale: 0.8}}
          animate={{opacity: 1, scale: 1}}
          className="text-center"
        >
          <motion.div
            className="w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl"
            animate={{rotate: 360}}
            transition={{duration: 2, repeat: Infinity, ease: "linear"}}
          >
            <span className="text-white font-bold text-2xl">A</span>
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">aiximo</h1>
          <p className="text-gray-400 mb-4">Initializing AI-powered development platform...</p>
          <div className="w-48 h-1 bg-gray-700 rounded-full mx-auto overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
              initial={{width: 0}}
              animate={{width: "100%"}}
              transition={{duration: 2, ease: "easeInOut"}}
            />
          </div>
          {dbConnected && (
            <motion.p
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              className="text-green-400 text-sm mt-3"
            >
              ✅ Database connected
            </motion.p>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-white">
        {/* Database Connection Status (for development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className={`fixed top-0 right-0 z-50 px-3 py-1 text-xs font-medium rounded-bl-lg ${
            dbConnected ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'
          }`}>
            {dbConnected ? '🟢 Supabase' : '🟡 LocalStorage'}
          </div>
        )}

        <Routes>
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              isAdminAuthenticated ? (
                <AdminDashboard onLogout={handleAdminLogout} />
              ) : (
                <AdminLogin onLogin={handleAdminLogin} />
              )
            }
          />

          {/* Public Routes with Navbar and Footer */}
          <Route
            path="/*"
            element={
              <>
                <Navbar />
                <motion.main
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  transition={{duration: 0.3}}
                >
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/case-studies" element={<CaseStudies />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:id" element={<BlogPost />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                  </Routes>
                </motion.main>
                <Footer />
              </>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;